import React from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import { stripeApi } from "@workspace/stripe";

import type { Order } from "../../types";
import {
	cardPaymentsToRecover,
	RECOVERY_RETRY_MS,
	shouldRetryCompletion,
} from "./card-payment-recovery";

/**
 * Payments already asked about, for the whole page: the hook is mounted by more than one provider, and
 * each would otherwise ask the server (and toast) for the same payment.
 */
const asked = new Set<string>();

/** How often the till looks again at payments that have only now been Pending long enough. */
const LOOK_AGAIN_MS = 10_000;

/**
 * HA-886 — a card payment the till started and never recorded (a reload or a dropped connection after
 * the card was tapped) stays Pending on its order, holding the bill. The till has lost its own record
 * of it, but the order has it: its payment id and the Stripe intent. This asks the server to record it
 * with the same call the live flow makes; the server checks the intent with Stripe and records it only
 * when Stripe has taken that money for that payment, so asking about one that was never taken is
 * refused (a 4xx) and left, and the server's own reconciler and webhook remain the backstop.
 */
export function useRecoverCardPayments({
	tenant,
	venueId,
	orders,
	activePaymentId,
}: {
	tenant: string;
	venueId: string;
	orders: Order[];
	activePaymentId?: string;
}) {
	const queryClient = useQueryClient();
	/** A payment whose last try got no answer: not asked again before this time. */
	const retryAt = React.useRef(new Map<string, number>());
	const [tick, setTick] = React.useState(0);

	React.useEffect(() => {
		const timer = setInterval(() => setTick((t) => t + 1), LOOK_AGAIN_MS);
		return () => clearInterval(timer);
	}, []);

	React.useEffect(() => {
		const now = Date.now();
		const skip = new Set(asked);
		for (const [id, at] of retryAt.current) if (at > now) skip.add(id);

		const due = cardPaymentsToRecover(orders, {
			activePaymentId,
			now,
			attempted: skip,
		});

		for (const payment of due) {
			const key = `${tenant}:${venueId}:${payment.paymentId}`;
			asked.add(payment.paymentId);

			stripeApi
				.completeCardPayment(tenant, venueId, payment.paymentId, {
					externalId: payment.externalId,
				})
				.then(() => {
					toast.success("A card payment has been recorded", {
						id: `recovered-${key}`,
						description:
							"Stripe had taken it, but it was never recorded on the order.",
					});
					void queryClient.invalidateQueries({
						queryKey: [tenant, venueId, "epos"],
					});
				})
				.catch((error: unknown) => {
					// A definite answer (Stripe says it was not taken) is left as it is. No answer is asked again, after a wait (the look-again tick).
					if (!shouldRetryCompletion(0, error)) return;

					asked.delete(payment.paymentId);
					retryAt.current.set(
						payment.paymentId,
						Date.now() + RECOVERY_RETRY_MS,
					);
				});
		}
	}, [orders, activePaymentId, tenant, venueId, tick, queryClient]);
}
